import { DateTime } from "luxon";
import { maxBy, sortBy } from "lodash";
import { AvailableSlotModel } from "app/models/scheduling/AvailableSlotModel";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";

export const defaultSelection = (bookLessonData, coachData = null, t = (s) => s) => {
  const { facilities, coaches, pricingValues } = bookLessonData;

  const { facilityId, id } = coachData || {};
  const public_link = window.location.pathname.split("/")[1] === "public";
  let selectedCoach;

  // CN-2744: an athlete can collaborate with more than one facility, and only
  // some of them have coaches they can book. aggregatedRates is keyed off coach
  // rows, so it has no entry for a coachless facility. Defaulting to
  // facilities[0] therefore selected a facility with no rates and no coaches,
  // which crashed the page. Prefer the first facility that has coaches.
  const facilityHasCoaches = (facility: any) =>
    (coaches?.[facility?.id] ?? []).length > 0;

  const selectedFacility = facilityId
    ? facilities.find((facility: any) => facility.id === facilityId) ??
      facilities[0]
    : facilities.find(facilityHasCoaches) ?? facilities[0];

  const facilityCoaches = sortBy(coaches?.[selectedFacility?.id], [
    "order_number",
  ]);

  if (public_link) {
    selectedCoach = facilityCoaches[0];
  } else {
    if (id) {
      selectedCoach = facilityCoaches.find(
        (coach: any) => coach.id === id
      );
    } else if (facilityCoaches.length === 1) {
      selectedCoach = facilityCoaches[0];
    } else {
      selectedCoach = {
        fullName: t("All Coaches"),
        value: "all-coaches",
        id: "all-coaches",
      };
    }
  }

  // No facility has coaches when the athlete has no bookable coach anywhere.
  // Keep the lookup safe so the page renders an empty picker instead of
  // throwing inside Object.values.
  const facilityRates =
    bookLessonData.aggregatedRates?.[selectedFacility?.id] ?? {};

  let selectedPricingValue =
    maxBy(Object.values(facilityRates), (rate: any) => {
      return rate.coachIds.length;
    }) || {};

  if (selectedCoach?.id !== "all-coaches") {
    selectedPricingValue = pricingValues?.[selectedCoach?.id]?.find(
      (rate) => rate.defaultRate
    );
  }

  const date = DateTime.utc().startOf("day");

  return {
    facility: selectedFacility,
    coach: selectedCoach,
    pricingValue: selectedPricingValue,
    date,
    duration: {},
  };
};

/**
 * The pricing entry for a custom lesson (a lesson with no booking option) on
 * the Edit Session page. It carries the length and the price of the lesson
 * itself. The id is null, so the slot request names no option.
 */
export const customPricingForLesson = (lesson) => {
  const minutes = Math.round(
    DateTime.fromISO(lesson.end).diff(DateTime.fromISO(lesson.start), "minutes")
      .minutes,
  );

  return {
    id: null,
    custom: true,
    title: "Custom",
    duration: `${minutes} mins`,
    durationInMinutes: minutes,
    rate: lesson.rate,
    location: lesson.location,
    hidePrice: lesson.lessonRateHidePrice,
    coachProfileId: lesson.coachProfile?.id,
  };
};

// Tells if a pricing entry is the entry of a custom lesson (see above).
export const isCustomPricing = (pricing: unknown): boolean =>
  (pricing as { custom?: boolean } | null | undefined)?.custom === true;

export const selectionForLesson = (bookLessonData, lesson) => {
  const { facilities, coaches } = bookLessonData;

  const selectedCoach = coaches[lesson.facilityId].find(
    (coach) => coach.id === lesson.coachProfile.id
  );

  const selectedFacility = facilities.find(
    (facility) => facility.id === lesson.facilityId
  );

  // A custom lesson has no option. Without this entry, the form fell back to
  // the default option of the coach: it showed that option, and it asked for
  // the slots of that option instead of the slots of the lesson.
  const selectedPricingValue =
    lesson.lessonRateId == null
      ? customPricingForLesson(lesson)
      : bookLessonData?.pricingValues[selectedCoach.id]?.find(
          (pv) => pv.id === lesson.lessonRateId,
        );

  const date = DateTime.fromISO(lesson.start, {
    zone: selectedCoach.timeZone,
  });

  return {
    facility: selectedFacility,
    coach: selectedCoach,
    pricingValue: selectedPricingValue,
    date,
    time: lesson.time,
  };
};

export const mapDays = (start, end) => {
  const days: Record<string, AvailableSlotModel[]> = {};

  while (start <= end) {
    days[start.toISODate()] = [];
    start = start.plus({ days: 1 });
  }

  return days;
};

/**
 * The slot params for the move of a custom lesson. With `lesson_schedule_id`,
 * the server offers the times of the lesson's own length that keep its held
 * bays free, the same rule as the save. Other requests get no extra param.
 */
export const customLessonSlotParams = (pricingValue, lesson) =>
  isCustomPricing(pricingValue) && lesson?.id != null
    ? { lesson_schedule_id: lesson.id }
    : {};

/**
 * The slot endpoint for a request. The coach endpoint needs an option. The
 * move of a custom lesson has no option, so it asks the endpoint that takes
 * `lesson_schedule_id`. A public page has no custom lesson to move.
 */
export const availableTimesPath = (params, isPublicPage: boolean) => {
  if (!isPublicPage && params.lesson_schedule_id != null) {
    return "/available_times";
  }

  return `/coach_profiles/${params.coach_profile_ids[0]}/available_times`;
};

export const getSingleCoachPublicTimeSlots = (
  params,
  initialAccumulator: Record<string, AvailableSlotModel[]>,
  isPublicPage: boolean,
) => {
  return schedulingFetchGet(availableTimesPath(params, isPublicPage), params, {}, null, isPublicPage)
    .then((rawSlots) => {
      if (!rawSlots[0].slots) {
        return initialAccumulator;
      }

      return rawSlots[0].slots
        ?.sort(
          (a, b) =>
            new Date(a.startHour).getTime() - new Date(b.startHour).getTime()
        )
        .reduce((accumulator, slot) => {
          const newSlot = new AvailableSlotModel(slot);
          const date = newSlot.startHour.toISODate();

          accumulator[date].push(newSlot);

          return accumulator;
        }, initialAccumulator);
    })
    .catch((e) => {
      console.error(e);
      return initialAccumulator;
    });
};
